/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type ConsultantInvoiceStatus } from '@therapy/shared-validators';

import { type ConsultantInvoice, consultantInvoicesApi } from '../api/consultant-invoices.js';

const KEY = ['consultant-invoices'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useMyConsultantInvoices(): UseQueryResult<readonly ConsultantInvoice[]> {
  return useQuery({ queryKey: [...KEY, 'mine'], queryFn: () => consultantInvoicesApi.mine() });
}

export function useConsultantInvoices(
  status?: ConsultantInvoiceStatus,
): UseQueryResult<readonly ConsultantInvoice[]> {
  return useQuery({
    queryKey: [...KEY, 'all', status ?? 'any'],
    queryFn: () => consultantInvoicesApi.list(status),
  });
}

export function useGenerateConsultantInvoice(): UseMutationResult<
  ConsultantInvoice,
  Error,
  string
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (month: string) => consultantInvoicesApi.generate(month),
    onSuccess: invalidate,
  });
}

export function useSubmitConsultantInvoice(): UseMutationResult<
  ConsultantInvoice,
  Error,
  { id: string; remarks?: string }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; remarks?: string }) =>
      consultantInvoicesApi.submit(v.id, v.remarks),
    onSuccess: invalidate,
  });
}

export function useApproveConsultantInvoice(): UseMutationResult<ConsultantInvoice, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => consultantInvoicesApi.approve(id),
    onSuccess: invalidate,
  });
}

export function useRejectConsultantInvoice(): UseMutationResult<
  ConsultantInvoice,
  Error,
  { id: string; note: string }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; note: string }) => consultantInvoicesApi.reject(v.id, v.note),
    onSuccess: invalidate,
  });
}

export function usePayConsultantInvoice(): UseMutationResult<
  ConsultantInvoice,
  Error,
  { id: string; paymentMode: Parameters<typeof consultantInvoicesApi.pay>[1] }
> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; paymentMode: Parameters<typeof consultantInvoicesApi.pay>[1] }) =>
      consultantInvoicesApi.pay(v.id, v.paymentMode),
    onSuccess: invalidate,
  });
}
